import React, {Suspense, lazy} from 'react';
import {Switch, Route, BrowserRouter} from 'react-router-dom';
import track from 'react-tracking';
import {FlagProvider} from '@unleash/proxy-client-react';
import {AppRoot} from '@tkww/orion-web-app-kernel';
import {ContentProvider} from '@tkww/reg-react-content-provider';
import {UNLEASH_CONFIG} from './constants';
import AppLayout from './components/AppLayout';
import ScrollToTop from './components/ScrollToTop';
import FullPageSpinner from './components/FullPageSpinner';
import {CategoryContextProvider} from './contexts/CategoryContext';
import {RouteResolverContextProvider} from './contexts/RouteResolverContext';
import {PlpScrollToPositionProvider} from './contexts/PlpScrollToPositionContext';
import {ModalProvider} from './contexts/ModalContext';
import {SnackbarContextProvider} from './contexts/SnackbarContext';
import trackSegmentEvent from './utils/trackSegmentEvent';
import ContentWrapper from './components/ContentWrapper';

const NoMatchRedirect = () =>
  window.location.replace(`${process.env.TK_HOST}/404`);

const Home = lazy(() => import('./views/Home'));
const ProductListPlp = lazy(() => import('./views/ProductListPlp'));
const Collections = lazy(() => import('./views/Collections'));
const CategoryPlp = lazy(() => import('./views/CategoryPlp'));
const StoryPlp = lazy(() => import('./views/StoryPlp'));
const SearchResults = lazy(() => import('./views/SearchResults'));
const ProductDetailsPage = lazy(() => import('./views/ProductDetailsPage'));
const PolicyPage = lazy(() => import('./views/PolicyPage'));
const Brands = lazy(() => import('./views/Brands'));
const BrandPlp = lazy(() => import('./views/BrandPlp'));
const ErrorPage = lazy(() => import('./views/ErrorPage'));
const PopularGiftsPlp = lazy(() => import('./views/PopularGiftsPlp'));
const TrendingProductsPlp = lazy(() => import('./views/TrendingProductsPlp'));
const RecentlyRegisteredPLP = lazy(() =>
  import('./views/RecentlyRegisteredPLP'),
);

const App = () => {
  return (
    <AppRoot
      settings={{
        brandDistManifest: 'theknot',
        serverRenderMode: 'none',
      }}>
      <BrowserRouter basename={process.env.PUBLIC_URL}>
        {/**
         * Place the RouteResolver and Category contexts inside the react router
         * tree so we can listen to url changes.
         */}
        <RouteResolverContextProvider>
          <CategoryContextProvider>
            <PlpScrollToPositionProvider>
              <ModalProvider>
                <ContentProvider>
                  <FlagProvider config={UNLEASH_CONFIG}>
                    <SnackbarContextProvider>
                      <AppLayout>
                        <ScrollToTop />
                        <Suspense fallback={<FullPageSpinner />}>
                          <Switch>
                            <Route component={Brands} exact path="/brands" />
                            <Route
                              component={PopularGiftsPlp}
                              exact
                              path="/popular-gifts"
                            />
                            <Route
                              component={BrandPlp}
                              exact
                              path="/brands/:brandName"
                            />
                            <Route
                              component={TrendingProductsPlp}
                              exact
                              path="/trending-products"
                            />
                            <Route
                              component={() => (
                                <ContentWrapper includeFiltersProvider>
                                  <ProductListPlp />
                                </ContentWrapper>
                              )}
                              exact
                              path={['/collections/:id', '/interests/:id']}
                            />
                            <Route
                              component={Collections}
                              exact
                              path="/collections"
                            />
                            <Route
                              component={() => (
                                <ContentWrapper>
                                  <Home />
                                </ContentWrapper>
                              )}
                              exact
                              path="/"
                            />
                            <Route
                              component={CategoryPlp}
                              exact
                              path="/categories/:id+"
                            />
                            <Route
                              component={StoryPlp}
                              exact
                              path="/stories/:id+"
                            />
                            <Route
                              component={SearchResults}
                              exact
                              path="/search"
                            />
                            <Route
                              component={ProductDetailsPage}
                              exact
                              path="/products/:productUrlKey"
                            />
                            <Route
                              component={PolicyPage}
                              exact
                              path="/static/:policyPageUrlKey"
                            />
                            <Route
                              component={RecentlyRegisteredPLP}
                              exact
                              path="/just-added"
                            />
                            <Route component={ErrorPage} exact path="/error" />
                            <Route component={NoMatchRedirect} />
                          </Switch>
                        </Suspense>
                      </AppLayout>
                    </SnackbarContextProvider>
                  </FlagProvider>
                </ContentProvider>
              </ModalProvider>
            </PlpScrollToPositionProvider>
          </CategoryContextProvider>
        </RouteResolverContextProvider>
      </BrowserRouter>
    </AppRoot>
  );
};

export default track(
  {},
  {
    dispatch: data => trackSegmentEvent(data),
  },
)(App);
